import React, { useState, useEffect, useRef } from 'react';
import { generateId } from '../utils/genIds';
import { ToolTip } from './FormInput';

// max file size in bytes (5MB)
const MAX_FILE_SIZE = 5000000;

const FileField = (props) => {
  const {
    label,
    initialValue,
    errors = [],
    name,
    hasMaxSize = true,
    buttonText = 'Change',
    previewType = 'image',
    acceptedTypes = [],
    toolTip,
    removePath, // URL to call to remove the attachment
    onRemove, // Callback when removed
  } = props;

  const id = generateId();
  const [image, setImage] = useState({ preview: initialValue, raw: '' });
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [markedForRemoval, setMarkedForRemoval] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (initialValue) {
      setImage({ preview: initialValue, raw: '' });
    }
  }, []);

  const handleClick = () => {
    fileInput.current.click();
  };

  const handleChange = (e) => {
    if (!e.target.files[0]) return;

    if (hasMaxSize && e.target.files[0].size > MAX_FILE_SIZE) {
      setError('File is too large. Please resize image before uploading.');
      return;
    }

    if (!validateFileType(e.target.files[0])) {
      setError(`File type must be one of ${acceptedTypes.join(', ')}.`);
      return;
    }

    if (previewType === 'name') setFileName(e.target.files[0].name);

    if (!!error) setError('');
    setMarkedForRemoval(false);

    setImage({
      preview: URL.createObjectURL(e.target.files[0]),
      raw: e.target.files[0],
    });
  };

  const validateFileType = (file) => {
    if (!acceptedTypes.length) return true;
    return acceptedTypes.includes(file.name.split('.').pop());
  };

  const handleRemove = async () => {
    if (removePath) {
      // If we have a remove path, call the API
      setIsRemoving(true);
      try {
        const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
        const response = await fetch(removePath, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        });

        if (response.ok) {
          setImage({ preview: '', raw: '' });
          setFileName('');
          if (onRemove) onRemove();
        } else {
          setError('Failed to remove image. Please try again.');
        }
      } catch (err) {
        setError('Failed to remove image. Please try again.');
      } finally {
        setIsRemoving(false);
      }
    } else {
      // Just clear locally and mark for removal on form submit
      setImage({ preview: '', raw: '' });
      setFileName('');
      setMarkedForRemoval(true);
      // Clear the file input
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    }
  };

  const hasImage = image.preview && !markedForRemoval;

  // Image preview layout
  if (previewType === 'image') {
    return (
      <div className="file-field-container">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {!!toolTip && <ToolTip dataForId={id} dataTip={toolTip} />}
        </label>

        {hasMaxSize && (
          <p className="text-xs text-gray-500 mb-3">
            Must be 5MB or smaller in size
          </p>
        )}

        <div className="relative">
          {hasImage ? (
            // Has image - show preview with overlay actions
            <div className="relative group">
              <div className="w-full aspect-square max-w-[200px] mx-auto bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200">
                <img
                  src={image.preview}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Hover overlay with actions */}
              <div className="absolute inset-0 max-w-[200px] mx-auto bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleClick}
                  className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
                  title="Change image"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={isRemoving}
                  className="p-2 bg-red-500 rounded-lg shadow-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                  title="Remove image"
                >
                  {isRemoving ? (
                    <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Mobile-friendly buttons below image */}
              <div className="flex items-center justify-center gap-2 mt-3 sm:hidden">
                <button
                  type="button"
                  onClick={handleClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Change
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={isRemoving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            // No image - show upload zone
            <div
              onClick={handleClick}
              className="w-full max-w-[200px] mx-auto aspect-square border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors cursor-pointer flex flex-col items-center justify-center"
            >
              <div className="p-3 bg-gray-200 rounded-full mb-3">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-600">Upload Image</span>
              <span className="text-xs text-gray-400 mt-1">Click to browse</span>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInput}
          type="file"
          name={name}
          id={id}
          accept="image/*"
          className="hidden"
          onChange={handleChange}
        />

        {/* Hidden input to mark for removal on form submit */}
        {markedForRemoval && (
          <input type="hidden" name={`${name.replace('[', '_remove_').replace(']', '')}`} value="1" />
        )}

        {/* Errors */}
        {(errors.length > 0 || error) && (
          <div className="mt-3 space-y-1">
            {errors.map((e, i) => (
              <p key={i} className="text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {e}
              </p>
            ))}
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Name preview layout (for non-image files)
  return (
    <div className="file-field-container">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
        {!!toolTip && <ToolTip dataForId={id} dataTip={toolTip} />}
      </label>

        {hasMaxSize && (
        <p className="text-xs text-gray-500 mb-2">
            Must be 5MB or smaller in size
          </p>
        )}

      <div className="flex items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            name={name}
            id={id}
          className="hidden"
            onChange={handleChange}
          />

          <button
          onClick={handleClick}
            type="button"
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          {fileName ? 'Change File' : buttonText}
          </button>

        {fileName && (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm text-gray-700 truncate max-w-[200px]">{fileName}</span>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Errors */}
      {(errors.length > 0 || error) && (
        <div className="mt-2 space-y-1">
          {errors.map((e, i) => (
            <p key={i} className="text-sm text-red-600">{e}</p>
          ))}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default FileField;
